'use client'

import { useEffect, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import posthog from 'posthog-js'

import { firstNameOf } from '@/lib/name'
import { Z2H_DIMENSIONS, DIMENSION_LABELS } from '@/lib/z2h/coverage'
import { SubmittedTakeover } from './ApplyChat'

// Friendly, plain-prose question copy per scorecard dimension — mirrors the
// AI interviewer's coverage intent, written for a founder reading a form
// instead of hearing a question. Deliberately low-stimulation (spec: "the
// form tab is the deliberately low-stimulation option").
const DIMENSION_PROMPTS = {
  founder_team:
    "Tell us about yourself and anyone building this with you — your background, relevant experience, and what makes your team right for this.",
  problem_market:
    'What problem are you solving, and who has it? Tell us about the market and why it matters now.',
  traction_validation:
    'What proof do you have that this works? Customers, users, pilots, letters of intent, revenue, waitlists — anything that shows real demand.',
  business_model:
    'How does the business make money? Walk us through your pricing and how revenue works.',
  financial_readiness:
    "What's your financial runway? Tell us about savings, funding, and how long you can sustain yourself while you build this.",
  tech_feasibility:
    "What does the technical build look like? What already exists, what's left to build, and who's building it?",
  capital_fit:
    'What kind of capital and support are you looking for, and why is Kingdom Kode the right fit for where you are?',
  commitment:
    "What's your commitment level and timeline — full-time or part-time, and when are you starting (or did you already)?",
}

function titleFor(dimension) {
  const label = DIMENSION_LABELS[dimension] || dimension.replace(/_/g, ' ')
  return label.charAt(0).toUpperCase() + label.slice(1)
}

// Step 1 = contact, steps 2–9 = one per dimension (spec §2). The final
// review screen sits after step 9 and isn't part of the "Step k of 9" count.
const STEP_KEYS = ['contact', ...Z2H_DIMENSIONS]
const REVIEW_INDEX = STEP_KEYS.length

const fieldClass =
  'w-full rounded-xl border border-white/10 bg-neutral-950/60 px-4 py-3 text-white placeholder-white/30 outline-none focus:border-kingdom-gold/60'

export function ApplyFormStepper({
  founderName = null,
  ventureName = null,
  founderEmail = null,
  initialComplete = false,
  initialRemaining = null,
  initialWebsiteUrl = null,
  initialFormAnswers = null,
  // Server-fresh { remaining, complete } snapshot from ApplyTabs, refreshed
  // on every tab switch — see the effect below.
  syncedState = null,
}) {
  const router = useRouter()
  const params = useSearchParams()
  const inviteToken = params.get('invite') || null
  const shouldReduceMotion = useReducedMotion()

  const startRemaining = Array.isArray(initialRemaining)
    ? initialRemaining
    : [...Z2H_DIMENSIONS]
  const startFormAnswers = initialFormAnswers || {}

  const [applicationId, setApplicationId] = useState(null)
  const [complete, setComplete] = useState(Boolean(initialComplete))
  const [remaining, setRemaining] = useState(startRemaining)
  const [contact, setContact] = useState({
    founderName: founderName || '',
    email: founderEmail || '',
    ventureName: ventureName || '',
  })
  const [websiteChoice, setWebsiteChoice] = useState(
    initialWebsiteUrl === 'none' ? 'no' : initialWebsiteUrl ? 'yes' : null,
  )
  const [websiteUrlInput, setWebsiteUrlInput] = useState(
    initialWebsiteUrl && initialWebsiteUrl !== 'none' ? initialWebsiteUrl : '',
  )
  const [answers, setAnswers] = useState(() => ({ ...startFormAnswers }))
  // The exact text last known to be persisted server-side, per dimension —
  // '' / absent means "nothing of ours saved yet". Drives the append-vs-
  // replace decision and lets unedited steps skip a redundant network call.
  const [savedText, setSavedText] = useState(() => ({ ...startFormAnswers }))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  // Cross-tab coverage sync: dimensions covered on the Chat tab mid-session
  // show their ✓ chip here (and unblock Next without typing), the submit
  // gate judges live coverage instead of the SSR snapshot, and a chat-side
  // completion flips this tab to the thank-you state. Props only seed once
  // at mount; this keeps us live across tab switches.
  useEffect(() => {
    if (!syncedState) return
    if (Array.isArray(syncedState.remaining)) setRemaining(syncedState.remaining)
    if (syncedState.complete) setComplete(true)
  }, [syncedState])

  // Land on: the contact step if identity is still missing; else the first
  // uncovered dimension; else the review step once everything is covered
  // but not yet submitted. Computed once — later navigation is explicit
  // (Back/Next/Edit), not re-derived from props.
  const [stepIndex, setStepIndex] = useState(() => {
    if (!founderName || !founderEmail) return 0
    const firstUncovered = Z2H_DIMENSIONS.findIndex((d) => startRemaining.includes(d))
    return firstUncovered === -1 ? REVIEW_INDEX : firstUncovered + 1
  })

  const firstName = firstNameOf(contact.founderName || founderName)

  async function postForm(extra) {
    setError(null)
    try {
      const res = await fetch('/api/z2h/intake', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'form',
          ...(inviteToken ? { inviteToken } : {}),
          ...(applicationId ? { applicationId } : {}),
          ...extra,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || 'Something went wrong')
      if (data.applicationId) setApplicationId(data.applicationId)
      // A form-started application mints its own inviteToken (route.js) —
      // thread it into the URL so a reload or a switch to the Chat tab
      // resumes the SAME application instead of losing it.
      if (data.inviteToken && data.inviteToken !== inviteToken) {
        const qp = new URLSearchParams(params.toString())
        qp.set('invite', data.inviteToken)
        router.replace(`?${qp.toString()}`, { scroll: false })
      }
      return data
    } catch (err) {
      setError(err.message)
      return null
    }
  }

  async function nextFromContact() {
    const name = contact.founderName.trim()
    const email = contact.email.trim()
    if (!name || !email) {
      setError('Your name and email are required to continue.')
      return
    }
    setBusy(true)
    const website =
      websiteChoice === 'yes' && websiteUrlInput.trim()
        ? { url: websiteUrlInput.trim() }
        : websiteChoice === 'no'
          ? { has: false }
          : undefined
    const res = await postForm({
      step: 'contact',
      founderName: name,
      email,
      ...(contact.ventureName.trim() ? { ventureName: contact.ventureName.trim() } : {}),
      ...(website ? { website } : {}),
    })
    setBusy(false)
    if (!res) return
    if (Array.isArray(res.remaining)) setRemaining(res.remaining)
    posthog.identify(email, {
      name,
      email,
    })
    posthog.capture('z2h_application_started', {
      has_venture_name: Boolean(contact.ventureName.trim()),
      has_website: websiteChoice === 'yes',
    })
    setStepIndex(1)
  }

  async function nextFromDimension(dimension, idx) {
    const text = (answers[dimension] || '').trim()
    const isCovered = !remaining.includes(dimension)
    if (!text && !isCovered) {
      setError('Please share something before moving on — even a couple sentences helps.')
      return
    }
    setError(null)
    const priorSaved = savedText[dimension] || ''
    if (text === priorSaved) {
      setStepIndex(idx + 1)
      return
    }
    setBusy(true)
    const res = await postForm({ dimension, message: text, replace: Boolean(priorSaved) })
    setBusy(false)
    if (!res) return
    setSavedText((s) => ({ ...s, [dimension]: text }))
    if (Array.isArray(res.remaining)) setRemaining(res.remaining)
    posthog.capture('z2h_application_dimension_saved', {
      dimension,
      answer_length: text.length,
    })
    setStepIndex(idx + 1)
  }

  async function submitApplication() {
    setBusy(true)
    const res = await postForm({ action: 'submit' })
    setBusy(false)
    if (!res) return
    posthog.capture('z2h_application_submitted', {
      venture_name: contact.ventureName || undefined,
    })
    setComplete(true)
  }

  if (complete) {
    return (
      <div className="min-h-0 flex-1 overflow-y-auto pb-4">
        <SubmittedTakeover firstName={firstName} email={contact.email || founderEmail} />
      </div>
    )
  }

  const isReview = stepIndex >= REVIEW_INDEX
  const dimension = !isReview && stepIndex > 0 ? Z2H_DIMENSIONS[stepIndex - 1] : null
  const dimensionCovered = dimension ? !remaining.includes(dimension) : false

  return (
    <div className="mt-6 flex min-h-0 flex-1 flex-col">
      {!isReview && (
        <div className="mb-4">
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 text-xs text-neutral-500">
            <span className="shrink-0">
              Step {stepIndex + 1} of {STEP_KEYS.length}
            </span>
            {dimensionCovered && (
              <span className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2.5 py-0.5 text-[0.65rem] font-medium text-emerald-300">
                ✓ covered — add more if you like
              </span>
            )}
          </div>
          <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full bg-kingdom-gold transition-all duration-300"
              style={{ width: `${((stepIndex + 1) / STEP_KEYS.length) * 100}%` }}
            />
          </div>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto rounded-3xl border border-white/10 bg-white/[0.03] p-5 sm:p-7">
        {/* Step-to-step crossfade — reactbits FadeContent's visual idiom
            (opacity + a few px of vertical drift) adapted to run on the
            framer-motion dependency already used across this app instead of
            FadeContent's literal implementation, which is scroll-trigger
            driven (gsap ScrollTrigger) and built for scroll-into-view
            reveals, not for a step-index-keyed mount/unmount transition —
            wiring it up here would fight its own trigger logic. Kept
            deliberately subtle (120ms, ~6px) so it reads as "next step"
            rather than a distracting flourish on the low-stimulation Form
            tab, and collapses to an instant cut under prefers-reduced-motion. */}
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={isReview ? 'review' : dimension || 'contact'}
            initial={shouldReduceMotion ? false : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={shouldReduceMotion ? undefined : { opacity: 0, y: -6 }}
            transition={{ duration: shouldReduceMotion ? 0 : 0.12, ease: 'easeOut' }}
          >
        {stepIndex === 0 && (
          <div>
            <h2 className="font-display text-xl font-medium">First, a quick intro</h2>
            <p className="mt-2 text-sm leading-6 text-white/60">
              Your application saves as you go — pause any time and pick up with the same link.
            </p>
            <div className="mt-6 space-y-3">
              <div>
                <label className="block text-xs font-medium text-neutral-400" htmlFor="form-founder-name">
                  Your name
                </label>
                <input
                  id="form-founder-name"
                  type="text"
                  autoComplete="name"
                  required
                  placeholder="Your name"
                  value={contact.founderName}
                  onChange={(e) => setContact((c) => ({ ...c, founderName: e.target.value }))}
                  className={`${fieldClass} mt-1.5`}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-400" htmlFor="form-founder-email">
                  Email address
                </label>
                <input
                  id="form-founder-email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  required
                  placeholder="you@company.com"
                  value={contact.email}
                  onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
                  className={`${fieldClass} mt-1.5`}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-400" htmlFor="form-venture-name">
                  Venture name <span className="text-neutral-600">(optional)</span>
                </label>
                <input
                  id="form-venture-name"
                  type="text"
                  autoComplete="organization"
                  placeholder="Venture name"
                  value={contact.ventureName}
                  onChange={(e) => setContact((c) => ({ ...c, ventureName: e.target.value }))}
                  className={`${fieldClass} mt-1.5`}
                />
              </div>
              <div>
                <p className="block text-xs font-medium text-neutral-400">
                  Do you already have a website? <span className="text-neutral-600">(optional)</span>
                </p>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setWebsiteChoice('yes')}
                    className={`min-h-11 rounded-full border px-4 py-2 text-sm font-medium transition ${
                      websiteChoice === 'yes'
                        ? 'border-kingdom-gold bg-kingdom-gold/10 text-kingdom-gold'
                        : 'border-white/15 bg-neutral-950 text-neutral-300 hover:border-white/40'
                    }`}
                  >
                    Yes, we do
                  </button>
                  <button
                    type="button"
                    onClick={() => setWebsiteChoice('no')}
                    className={`min-h-11 rounded-full border px-4 py-2 text-sm font-medium transition ${
                      websiteChoice === 'no'
                        ? 'border-kingdom-gold bg-kingdom-gold/10 text-kingdom-gold'
                        : 'border-white/15 bg-neutral-950 text-neutral-300 hover:border-white/40'
                    }`}
                  >
                    Not yet
                  </button>
                </div>
                {websiteChoice === 'yes' && (
                  <input
                    type="text"
                    inputMode="url"
                    autoComplete="url"
                    aria-label="Your website address"
                    placeholder="yourcompany.com"
                    value={websiteUrlInput}
                    onChange={(e) => setWebsiteUrlInput(e.target.value)}
                    className={`${fieldClass} mt-2`}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {dimension && (
          <div>
            <h2 className="font-display text-xl font-medium">{titleFor(dimension)}</h2>
            <p className="mt-2 text-sm leading-6 text-white/60">{DIMENSION_PROMPTS[dimension]}</p>
            <textarea
              value={answers[dimension] || ''}
              onChange={(e) => setAnswers((a) => ({ ...a, [dimension]: e.target.value }))}
              rows={8}
              maxLength={2000}
              placeholder="Write as much or as little as feels right…"
              aria-label={titleFor(dimension)}
              className="mt-5 w-full resize-y rounded-xl border border-white/10 bg-neutral-950/60 px-4 py-3 text-base leading-relaxed text-white placeholder-white/30 outline-none focus:border-kingdom-gold/60 sm:text-sm"
            />
          </div>
        )}

        {isReview && (
          <ReviewStep
            contact={contact}
            websiteChoice={websiteChoice}
            websiteUrlInput={websiteUrlInput}
            answers={answers}
            remaining={remaining}
            onEditContact={() => setStepIndex(0)}
            onEditDimension={(idx) => setStepIndex(idx)}
          />
        )}
          </motion.div>
        </AnimatePresence>
      </div>

      {error && <p className="mt-3 text-center text-sm text-red-400">{error}</p>}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
          disabled={stepIndex === 0 || busy}
          className="min-h-11 shrink-0 rounded-full border border-white/15 px-5 py-2.5 text-sm font-medium text-neutral-300 transition hover:border-white/40 disabled:opacity-40"
        >
          Back
        </button>
        {!isReview ? (
          <button
            type="button"
            onClick={stepIndex === 0 ? nextFromContact : () => nextFromDimension(dimension, stepIndex)}
            disabled={busy}
            className="min-h-11 shrink-0 rounded-full bg-kingdom-gold px-6 py-2.5 text-sm font-semibold text-neutral-950 transition hover:bg-[#d9bd65] disabled:opacity-60"
          >
            {busy ? 'Saving…' : 'Next'}
          </button>
        ) : (
          <button
            type="button"
            onClick={submitApplication}
            disabled={busy || remaining.length > 0}
            title={remaining.length > 0 ? 'Every section needs at least a short answer first' : undefined}
            className="min-h-11 shrink-0 rounded-full bg-kingdom-gold px-6 py-2.5 text-sm font-semibold text-neutral-950 transition hover:bg-[#d9bd65] disabled:opacity-40"
          >
            {busy ? 'Submitting…' : 'Submit application'}
          </button>
        )}
      </div>
    </div>
  )
}

function ReviewStep({
  contact,
  websiteChoice,
  websiteUrlInput,
  answers,
  remaining,
  onEditContact,
  onEditDimension,
}) {
  return (
    <div>
      <h2 className="font-display text-xl font-medium">Review your answers</h2>
      <p className="mt-2 text-sm leading-6 text-white/60">
        Everything below goes to our team. Tap any section to edit it before you submit.
      </p>

      {remaining.length > 0 && (
        <p className="mt-4 rounded-xl border border-amber-400/30 bg-amber-400/[0.06] px-4 py-3 text-sm text-amber-200">
          A few areas still need at least a short answer:{' '}
          {remaining.map((d) => titleFor(d)).join(', ')}.
        </p>
      )}

      <button
        type="button"
        onClick={onEditContact}
        className="mt-6 w-full rounded-xl border border-white/10 bg-white/[0.02] p-4 text-left transition hover:border-kingdom-gold/40"
      >
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold tracking-widest text-kingdom-gold uppercase">
            Contact
          </span>
          <span className="text-xs text-neutral-500">Edit</span>
        </div>
        <p className="mt-2 text-sm text-white">
          {contact.founderName || '—'} · {contact.email || '—'}
          {contact.ventureName ? ` · ${contact.ventureName}` : ''}
        </p>
        <p className="mt-1 text-xs text-neutral-500">
          {websiteChoice === 'yes'
            ? websiteUrlInput || 'Has a website'
            : websiteChoice === 'no'
              ? "Doesn't have a website yet"
              : 'Website not answered'}
        </p>
      </button>

      <div className="mt-4 space-y-3">
        {Z2H_DIMENSIONS.map((d, idx) => {
          const text = (answers[d] || '').trim()
          const covered = !remaining.includes(d)
          return (
            <button
              type="button"
              key={d}
              onClick={() => onEditDimension(idx + 1)}
              className="w-full rounded-xl border border-white/10 bg-white/[0.02] p-4 text-left transition hover:border-kingdom-gold/40"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="min-w-0 truncate text-xs font-semibold tracking-widest text-kingdom-gold uppercase">
                  {titleFor(d)}
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  {covered && (
                    <span className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2 py-0.5 text-[0.6rem] font-medium whitespace-nowrap text-emerald-300">
                      ✓ covered
                    </span>
                  )}
                  <span className="text-xs text-neutral-500">Edit</span>
                </span>
              </div>
              <p className="mt-2 line-clamp-3 text-sm break-words whitespace-pre-wrap text-white/80">
                {text || (covered ? 'Answered by conversation — add more if you like.' : 'Not answered yet.')}
              </p>
            </button>
          )
        })}
      </div>
    </div>
  )
}
